/**
 * Evaluation Service Contract
 *
 * Defines the interface for the Evaluation Service (16-eval-framework-service).
 * Must match: 16-eval-framework-service/proto/evaluation.proto
 *
 * BREAKING CHANGE LOG:
 * - None yet. Initial version.
 */

import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

export enum EvaluationStatus {
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum RegressionSeverity {
  CRITICAL = 'CRITICAL', // > 20% drop
  HIGH = 'HIGH', // > 5% drop
  MEDIUM = 'MEDIUM', // > 1% drop
  LOW = 'LOW', // < 1% drop
}

// ============================================================================
// REQUEST MESSAGES
// ============================================================================

export interface CreateTestCaseRequest {
  business_id: string;
  test_suite_id: string;
  input: string;
  expected_output: string;
  description?: string;
  metadata?: Record<string, string>;
}

export interface RunEvaluationRequest {
  business_id: string;
  model_id: string;
  test_suite_id: string;
  max_parallel?: number;
  timeout_seconds?: number;
}

export interface GetEvaluationResultsRequest {
  business_id: string;
  run_id: string;
}

export interface StreamEvaluationResultsRequest {
  business_id: string;
  run_id: string;
}

export interface ListTestCasesRequest {
  business_id: string;
  test_suite_id: string;
  limit?: number;
  offset?: number;
}

export interface GetTestCaseRequest {
  business_id: string;
  test_case_id: string;
}

export interface SetBaselineRequest {
  business_id: string;
  model_id: string;
  test_suite_id: string;
  run_id: string;
  threshold?: number;
}

export interface GetBaselineRequest {
  business_id: string;
  model_id: string;
}

export interface CheckRegressionRequest {
  business_id: string;
  model_id: string;
  test_suite_id: string;
  current_run_id: string;
}

export interface GetRegressionHistoryRequest {
  business_id: string;
  model_id: string;
  limit?: number;
}

// ============================================================================
// RESPONSE MESSAGES
// ============================================================================

export interface CreateTestCaseResponse {
  test_case_id: string;
  created_at: number;
}

export interface RunEvaluationResponse {
  run_id: string;
  status: EvaluationStatus;
  started_at: number;
}

export interface EvaluationResult {
  test_case_id: string;
  score: number; // 0.0 - 1.0
  passed: boolean;
  reasoning: string; // LLM-as-Judge explanation
  actual_output: string;
  duration_ms: number;
}

export interface GetEvaluationResultsResponse {
  run_id: string;
  status: EvaluationStatus;
  results: EvaluationResult[];
  average_score: number;
  pass_rate: number; // Percentage of passed tests
  completed_at?: number;
  error_message?: string;
}

export interface EvaluationResultUpdate {
  run_id: string;
  test_case_id: string;
  result: EvaluationResult;
  progress: number; // Percentage complete
}

export interface TestCaseInfo {
  test_case_id: string;
  input: string;
  expected_output: string;
  description?: string;
  created_at: number;
}

export interface ListTestCasesResponse {
  test_cases: TestCaseInfo[];
  total: number;
}

export interface GetTestCaseResponse {
  test_case_id: string;
  test_suite_id: string;
  input: string;
  expected_output: string;
  description?: string;
  metadata?: Record<string, string>;
  created_at: number;
}

export interface GetBaselineResponse {
  model_id: string;
  test_suite_id: string;
  baseline_run_id: string;
  baseline_score: number;
  baseline_pass_rate: number;
  threshold: number;
  created_at: number;
}

export interface CheckRegressionResponse {
  regression: boolean;
  baseline_score: number;
  current_score: number;
  percentage_change: number; // e.g., -0.15 for 15% drop
  regression_cases: string[]; // Test IDs that regressed
  severity: number; // 0.0 - 1.0
}

export interface RegressionEntry {
  run_id: string;
  score: number;
  regression_detected: boolean;
  percentage_change: number;
  timestamp: number;
}

export interface GetRegressionHistoryResponse {
  entries: RegressionEntry[];
}

// ============================================================================
// ZOD SCHEMAS FOR VALIDATION
// ============================================================================

export const createTestCaseRequestSchema = z.object({
  business_id: z.string().min(1, 'Business ID is required'),
  test_suite_id: z.string().min(1, 'Test suite ID is required'),
  input: z.string().min(1, 'Input is required'),
  expected_output: z.string().min(1, 'Expected output is required'),
  description: z.string().optional(),
  metadata: z.record(z.string(), z.string()).optional(),
});

export const runEvaluationRequestSchema = z.object({
  business_id: z.string().min(1, 'Business ID is required'),
  model_id: z.string().min(1, 'Model ID is required'),
  test_suite_id: z.string().min(1, 'Test suite ID is required'),
  max_parallel: z.number().min(1).optional().default(4),
  timeout_seconds: z.number().min(1).optional().default(30),
});

export const getEvaluationResultsRequestSchema = z.object({
  business_id: z.string().min(1, 'Business ID is required'),
  run_id: z.string().min(1, 'Run ID is required'),
});

export const listTestCasesRequestSchema = z.object({
  business_id: z.string().min(1, 'Business ID is required'),
  test_suite_id: z.string().min(1, 'Test suite ID is required'),
  limit: z.number().min(1).optional().default(100),
  offset: z.number().min(0).optional().default(0),
});

export const getTestCaseRequestSchema = z.object({
  business_id: z.string().min(1, 'Business ID is required'),
  test_case_id: z.string().min(1, 'Test case ID is required'),
});

export const setBaselineRequestSchema = z.object({
  business_id: z.string().min(1, 'Business ID is required'),
  model_id: z.string().min(1, 'Model ID is required'),
  test_suite_id: z.string().min(1, 'Test suite ID is required'),
  run_id: z.string().min(1, 'Run ID is required'),
  threshold: z.number().min(0).max(1).optional().default(0.01),
});

export const getBaselineRequestSchema = z.object({
  business_id: z.string().min(1, 'Business ID is required'),
  model_id: z.string().min(1, 'Model ID is required'),
});

export const checkRegressionRequestSchema = z.object({
  business_id: z.string().min(1, 'Business ID is required'),
  model_id: z.string().min(1, 'Model ID is required'),
  test_suite_id: z.string().min(1, 'Test suite ID is required'),
  current_run_id: z.string().min(1, 'Current run ID is required'),
});

export const getRegressionHistoryRequestSchema = z.object({
  business_id: z.string().min(1, 'Business ID is required'),
  model_id: z.string().min(1, 'Model ID is required'),
  limit: z.number().min(1).optional().default(10),
});

// ============================================================================
// DEEPEVAL METRICS (Issue #261 - Phase 2)
// ============================================================================

/**
 * DeepEval Metric Types
 *
 * Based on DeepEval's LLM evaluation metrics:
 * - Answer Relevancy: How relevant is the answer to the query
 * - Faithfulness: How faithful is the answer to the retrieved context
 * - Contextual Precision: Are relevant context items ranked higher
 * - Contextual Recall: What proportion of relevant context is retrieved
 *
 * @see https://docs.confident-ai.com/docs/metrics-introduction
 */
export enum DeepEvalMetricType {
  ANSWER_RELEVANCY = 'ANSWER_RELEVANCY',
  FAITHFULNESS = 'FAITHFULNESS',
  CONTEXTUAL_PRECISION = 'CONTEXTUAL_PRECISION',
  CONTEXTUAL_RECALL = 'CONTEXTUAL_RECALL',
  HALLUCINATION = 'HALLUCINATION',
  TOXICITY = 'TOXICITY',
  BIAS = 'BIAS',
  SUMMARIZATION = 'SUMMARIZATION',
}

/**
 * RAGAS Metric Types
 *
 * Based on RAGAS (Retrieval Augmented Generation Assessment):
 * - Faithfulness: Factual consistency with context
 * - Answer Relevancy: Relevance to the question
 * - Context Precision: Signal-to-noise ratio in context
 * - Context Recall: Coverage of ground truth
 *
 * @see https://docs.ragas.io/en/latest/concepts/metrics/
 */
export enum RagasMetricType {
  FAITHFULNESS = 'FAITHFULNESS',
  ANSWER_RELEVANCY = 'ANSWER_RELEVANCY',
  CONTEXT_PRECISION = 'CONTEXT_PRECISION',
  CONTEXT_RECALL = 'CONTEXT_RECALL',
  CONTEXT_ENTITY_RECALL = 'CONTEXT_ENTITY_RECALL',
  ANSWER_SIMILARITY = 'ANSWER_SIMILARITY',
  ANSWER_CORRECTNESS = 'ANSWER_CORRECTNESS',
}

export interface RAGEvaluationInput {
  query: string;
  answer: string;
  contexts: string[];
  groundTruth?: string;
}

export interface MetricScore {
  metric: DeepEvalMetricType | RagasMetricType;
  score: number; // 0.0 - 1.0
  passed: boolean;
  threshold: number;
  reason?: string;
  details?: Record<string, unknown>;
}

export interface DeepEvalRequest {
  businessId: string;
  input: RAGEvaluationInput;
  metrics: DeepEvalMetricType[];
  thresholds?: Record<DeepEvalMetricType, number>;
  modelId?: string; // Judge model for evaluation
}

export interface DeepEvalResponse {
  scores: MetricScore[];
  overallScore: number;
  passed: boolean;
  evaluationId: string;
  durationMs: number;
}

export interface RagasEvaluationRequest {
  businessId: string;
  input: RAGEvaluationInput;
  metrics: RagasMetricType[];
  thresholds?: Record<RagasMetricType, number>;
}

export interface RagasEvaluationResponse {
  scores: MetricScore[];
  overallScore: number;
  passed: boolean;
  evaluationId: string;
  durationMs: number;
}

// ============================================================================
// CONTINUOUS EVALUATION PIPELINE (Issue #261 - Phase 2)
// ============================================================================

export enum EvaluationPipelineStatus {
  IDLE = 'IDLE',
  RUNNING = 'RUNNING',
  PAUSED = 'PAUSED',
  FAILED = 'FAILED',
}

export interface EvaluationPipelineConfig {
  businessId: string;
  name: string;
  description?: string;
  enabled: boolean;
  schedule?: string; // Cron expression
  triggerOnDeploy: boolean;
  metrics: (DeepEvalMetricType | RagasMetricType)[];
  thresholds: Record<string, number>;
  alertOnRegression: boolean;
  alertThreshold: number; // Percentage drop to trigger alert
  notificationChannels: string[];
}

export interface EvaluationPipelineRun {
  pipelineId: string;
  runId: string;
  status: EvaluationPipelineStatus;
  startedAt: number;
  completedAt?: number;
  totalTests: number;
  passedTests: number;
  failedTests: number;
  averageScore: number;
  regressionDetected: boolean;
  results: MetricScore[];
}

export interface CreatePipelineRequest {
  businessId: string;
  config: Omit<EvaluationPipelineConfig, 'businessId'>;
}

export interface CreatePipelineResponse {
  pipelineId: string;
  createdAt: number;
}

export interface TriggerPipelineRequest {
  businessId: string;
  pipelineId: string;
  testSuiteId?: string;
  modelId?: string;
}

export interface TriggerPipelineResponse {
  runId: string;
  status: EvaluationPipelineStatus;
}

export interface GetPipelineRunRequest {
  businessId: string;
  pipelineId: string;
  runId: string;
}

export interface GetPipelineRunResponse {
  run: EvaluationPipelineRun;
}

// ============================================================================
// ZOD SCHEMAS FOR DEEPEVAL/RAGAS
// ============================================================================

export const ragEvaluationInputSchema = z.object({
  query: z.string().min(1, 'Query is required'),
  answer: z.string().min(1, 'Answer is required'),
  contexts: z.array(z.string()).min(1, 'At least one context is required'),
  groundTruth: z.string().optional(),
});

export const deepEvalRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  input: ragEvaluationInputSchema,
  metrics: z.array(z.nativeEnum(DeepEvalMetricType)).min(1, 'At least one metric required'),
  thresholds: z.record(z.nativeEnum(DeepEvalMetricType), z.number().min(0).max(1)).optional(),
  modelId: z.string().optional(),
});

export const ragasEvaluationRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  input: ragEvaluationInputSchema,
  metrics: z.array(z.nativeEnum(RagasMetricType)).min(1, 'At least one metric required'),
  thresholds: z.record(z.nativeEnum(RagasMetricType), z.number().min(0).max(1)).optional(),
});

export const evaluationPipelineConfigSchema = z.object({
  name: z.string().min(1, 'Pipeline name is required'),
  description: z.string().optional(),
  enabled: z.boolean().default(true),
  schedule: z.string().optional(),
  triggerOnDeploy: z.boolean().default(true),
  metrics: z.array(z.string()).min(1, 'At least one metric required'),
  thresholds: z.record(z.string(), z.number().min(0).max(1)),
  alertOnRegression: z.boolean().default(true),
  alertThreshold: z.number().min(0).max(1).default(0.05),
  notificationChannels: z.array(z.string()).default([]),
});

export const createPipelineRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  config: evaluationPipelineConfigSchema,
});

export const triggerPipelineRequestSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  pipelineId: z.string().min(1, 'Pipeline ID is required'),
  testSuiteId: z.string().optional(),
  modelId: z.string().optional(),
});

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_DEEPEVAL_THRESHOLDS: Record<DeepEvalMetricType, number> = {
  [DeepEvalMetricType.ANSWER_RELEVANCY]: 0.7,
  [DeepEvalMetricType.FAITHFULNESS]: 0.8,
  [DeepEvalMetricType.CONTEXTUAL_PRECISION]: 0.7,
  [DeepEvalMetricType.CONTEXTUAL_RECALL]: 0.7,
  [DeepEvalMetricType.HALLUCINATION]: 0.5, // Lower = less hallucination
  [DeepEvalMetricType.TOXICITY]: 0.1, // Lower = less toxic
  [DeepEvalMetricType.BIAS]: 0.2, // Lower = less bias
  [DeepEvalMetricType.SUMMARIZATION]: 0.7,
};

export const DEFAULT_RAGAS_THRESHOLDS: Record<RagasMetricType, number> = {
  [RagasMetricType.FAITHFULNESS]: 0.8,
  [RagasMetricType.ANSWER_RELEVANCY]: 0.7,
  [RagasMetricType.CONTEXT_PRECISION]: 0.7,
  [RagasMetricType.CONTEXT_RECALL]: 0.7,
  [RagasMetricType.CONTEXT_ENTITY_RECALL]: 0.6,
  [RagasMetricType.ANSWER_SIMILARITY]: 0.7,
  [RagasMetricType.ANSWER_CORRECTNESS]: 0.7,
};
